/**
 * ที่อยู่แบบแยกช่อง (บ้านเลขที่ · ตำบล/แขวง · อำเภอ/เขต · จังหวัด · รหัสไปรษณีย์)
 *
 * ⚠️ ไฟล์นี้มี 2 สำเนาที่ต้องเหมือนกันทุกตัวอักษร:
 *   - Admin:    components/AdminIslands/features/thaiAddress/thaiAddressLogic.ts
 *   - OrderCus: ordercus-web/src/lib/thaiAddress.ts
 * แก้ที่หนึ่งต้องคัดลอกไปอีกที่ (test ทั้งสองฝั่งใช้ชุดเดียวกัน)
 *
 * หลักการเก็บข้อมูล: ช่องข้อความเดิม (CustomerAddress / address) ยังเป็นข้อความเต็มเสมอ
 * ข้อมูลแยกช่องเก็บคู่ใน AddressParts / addressParts — ระบบที่อ่านข้อความเดิมจึงไม่ต้องแก้
 * ใช้ข้อมูลแยกช่องเฉพาะเมื่อ composeAddress(parts) ตรงกับข้อความปัจจุบัน (partsMatchingText)
 * กันกรณีมีจุดอื่นแก้แค่ข้อความ แล้วข้อมูลแยกช่องค้างของเก่า
 */

export type AddressParts = {
  /** บ้านเลขที่ หมู่ ซอย ถนน */
  line: string;
  subdistrict: string;
  district: string;
  province: string;
  postcode: string;
};

/** ข้อมูลดิบ: [จังหวัด, [[อำเภอ, [[ตำบล, รหัสไปรษณีย์], …]], …]] — ชื่อไม่มีคำนำหน้า (เขต/แขวง/ต./อ./จ.) */
export type ThaiAddressRaw = Array<[string, Array<[string, Array<[string, number | string]>]>]>;

export type ThaiAddressEntry = { province: string; district: string; subdistrict: string; postcode: string };

export type ThaiAddressDb = {
  provinces: string[];
  /** จังหวัด → อำเภอ → [[ตำบล, รหัส]] */
  tree: Map<string, Map<string, Array<[string, string]>>>;
  byZip: Map<string, ThaiAddressEntry[]>;
  entries: ThaiAddressEntry[];
};

export const BANGKOK = 'กรุงเทพมหานคร';

const LIMITS = { line: 300, name: 80 };

function clean(value: unknown, max: number): string {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().slice(0, max);
}

export function emptyAddressParts(): AddressParts {
  return { line: '', subdistrict: '', district: '', province: '', postcode: '' };
}

/** รับค่าที่มาจาก Firestore/ฟอร์ม (อาจผิดรูป) → AddressParts หรือ null ถ้าไม่มีอะไรเลย */
export function normalizeAddressParts(input: unknown): AddressParts | null {
  if (!input || typeof input !== 'object') return null;
  const v = input as Record<string, unknown>;
  const parts: AddressParts = {
    line: clean(v.line, LIMITS.line),
    subdistrict: clean(v.subdistrict, LIMITS.name),
    district: clean(v.district, LIMITS.name),
    province: clean(v.province, LIMITS.name),
    postcode: String(v.postcode == null ? '' : v.postcode).replace(/\D/g, '').slice(0, 5),
  };
  return Object.values(parts).some(Boolean) ? parts : null;
}

export function isAddressPartsComplete(parts: AddressParts | null | undefined): boolean {
  return !!parts && !!(parts.line && parts.subdistrict && parts.district && parts.province && /^\d{5}$/.test(parts.postcode));
}

export function isBangkok(province: string): boolean {
  return String(province || '').trim() === BANGKOK;
}

/** คำนำหน้าตามจังหวัด — กรุงเทพฯ ใช้ แขวง/เขต ไม่มี ต. อ. จ. */
export function addressFieldLabels(province: string): { district: string; subdistrict: string } {
  return isBangkok(province)
    ? { district: 'เขต', subdistrict: 'แขวง' }
    : { district: 'อำเภอ / เขต', subdistrict: 'ตำบล / แขวง' };
}

function subdistrictText(p: AddressParts): string {
  if (!p.subdistrict) return '';
  return (isBangkok(p.province) ? 'แขวง' : 'ต.') + p.subdistrict;
}
function districtText(p: AddressParts): string {
  if (!p.district) return '';
  return (isBangkok(p.province) ? 'เขต' : 'อ.') + p.district;
}
function provinceText(p: AddressParts): string {
  if (!p.province) return '';
  return isBangkok(p.province) ? p.province : 'จ.' + p.province;
}

/** ข้อความเต็มที่เก็บลงช่องเดิม เช่น "45 ถ.ห้วยแก้ว ต.สุเทพ อ.เมืองเชียงใหม่ จ.เชียงใหม่ 50200" */
export function composeAddress(parts: AddressParts | null | undefined): string {
  if (!parts) return '';
  return [clean(parts.line, LIMITS.line), subdistrictText(parts), districtText(parts), provinceText(parts), parts.postcode]
    .filter(Boolean)
    .join(' ');
}

/**
 * บรรทัดสำหรับฉลาก — ไม่เกิน 3 บรรทัด
 * 1) บ้านเลขที่ + ตำบล  2) อำเภอ + จังหวัด  3) รหัสไปรษณีย์ (แสดงตัวใหญ่)
 */
export function addressLabelLines(parts: AddressParts): { lines: string[]; postcode: string } {
  const l1 = [clean(parts.line, LIMITS.line), subdistrictText(parts)].filter(Boolean).join(' ');
  const l2 = [districtText(parts), provinceText(parts)].filter(Boolean).join(' ');
  return { lines: [l1, l2].filter(Boolean), postcode: parts.postcode };
}

function squash(text: string): string {
  return String(text || '').replace(/\s+/g, '');
}

/** ข้อมูลแยกช่องที่ "ยังตรง" กับข้อความปัจจุบัน — ไม่ตรง = null (ใช้ข้อความเดิม) */
export function partsMatchingText(text: unknown, ...candidates: unknown[]): AddressParts | null {
  const t = squash(String(text || ''));
  if (!t) return null;
  for (const c of candidates) {
    const parts = normalizeAddressParts(c);
    if (parts && isAddressPartsComplete(parts) && squash(composeAddress(parts)) === t) return parts;
  }
  return null;
}

// ────────────────────────── ฐานข้อมูลรหัสไปรษณีย์ ──────────────────────────

export function buildThaiAddressDb(raw: ThaiAddressRaw): ThaiAddressDb {
  const tree = new Map<string, Map<string, Array<[string, string]>>>();
  const byZip = new Map<string, ThaiAddressEntry[]>();
  const entries: ThaiAddressEntry[] = [];
  for (const [province, districts] of Array.isArray(raw) ? raw : []) {
    const dm = new Map<string, Array<[string, string]>>();
    for (const [district, subs] of districts || []) {
      const list: Array<[string, string]> = [];
      for (const [subdistrict, zipRaw] of subs || []) {
        const postcode = String(zipRaw).padStart(5, '0');
        list.push([subdistrict, postcode]);
        const entry = { province, district, subdistrict, postcode };
        entries.push(entry);
        const z = byZip.get(postcode);
        if (z) z.push(entry);
        else byZip.set(postcode, [entry]);
      }
      dm.set(district, list);
    }
    tree.set(province, dm);
  }
  return { provinces: Array.from(tree.keys()), tree, byZip, entries };
}

let dbPromise: Promise<ThaiAddressDb> | null = null;
let dbLoaded: ThaiAddressDb | null = null;

/** โหลดไฟล์ข้อมูลครั้งเดียวต่อหน้า (ไม่อ่าน Firestore) · โหลดไม่สำเร็จ = ลองใหม่ได้ครั้งถัดไป */
export function loadThaiAddressDb(url: string): Promise<ThaiAddressDb> {
  if (dbLoaded) return Promise.resolve(dbLoaded);
  if (!dbPromise) {
    dbPromise = fetch(url, { cache: 'force-cache' })
      .then((res) => {
        if (!res.ok) throw new Error('thai-address ' + res.status);
        return res.json() as Promise<ThaiAddressRaw>;
      })
      .then((raw) => {
        dbLoaded = buildThaiAddressDb(raw);
        return dbLoaded;
      })
      .catch((err) => {
        dbPromise = null;
        throw err;
      });
  }
  return dbPromise;
}

export function loadedThaiAddressDb(): ThaiAddressDb | null {
  return dbLoaded;
}

/** ใช้ในเทสต์ */
export function setThaiAddressDbForTest(db: ThaiAddressDb | null): void {
  dbLoaded = db;
  dbPromise = null;
}

function uniq(list: string[]): string[] {
  return Array.from(new Set(list));
}

export type ZipState = 'empty' | 'partial' | 'found' | 'unknown';

export type AddressSelection = {
  /** ค่าหลังเลือกให้อัตโนมัติ (ตัวเลือกเดียว = เลือกให้เลย) */
  parts: AddressParts;
  provinces: string[];
  districts: string[];
  subdistricts: string[];
  zipState: ZipState;
  /** จำนวนอำเภอของรหัสนี้ (ไว้บอกผู้ใช้) */
  zipDistrictCount: number;
};

/**
 * ตัวเลือก dropdown แบบสัมพันธ์กัน
 * - มีรหัสไปรษณีย์ครบ 5 หลักที่รู้จัก → จำกัดตัวเลือกตามรหัส · ตัวเลือกเดียวเลือกให้เลย
 * - ไม่มี/ไม่รู้จักรหัส → เลือก จังหวัด → อำเภอ → ตำบล เอง แล้วเติมรหัสให้
 */
export function resolveAddressSelection(db: ThaiAddressDb, input: AddressParts): AddressSelection {
  const parts: AddressParts = { ...input };
  const zip = String(parts.postcode || '').replace(/\D/g, '').slice(0, 5);
  parts.postcode = zip;
  const cands = zip.length === 5 ? db.byZip.get(zip) || [] : [];
  const zipState: ZipState = !zip ? 'empty' : zip.length < 5 ? 'partial' : cands.length ? 'found' : 'unknown';

  if (zipState === 'found') {
    const provinces = uniq(cands.map((c) => c.province));
    if (!provinces.includes(parts.province)) parts.province = provinces.length === 1 ? provinces[0] : '';
    const inProv = cands.filter((c) => c.province === parts.province);
    const districts = uniq(inProv.map((c) => c.district));
    if (!districts.includes(parts.district)) parts.district = districts.length === 1 ? districts[0] : '';
    const subdistricts = uniq(inProv.filter((c) => c.district === parts.district).map((c) => c.subdistrict));
    if (!subdistricts.includes(parts.subdistrict)) parts.subdistrict = subdistricts.length === 1 ? subdistricts[0] : '';
    return { parts, provinces, districts, subdistricts, zipState, zipDistrictCount: uniq(cands.map((c) => c.province + '|' + c.district)).length };
  }

  const provinces = db.provinces;
  if (!db.tree.has(parts.province)) parts.province = '';
  const dm = parts.province ? db.tree.get(parts.province) : undefined;
  const districts = dm ? Array.from(dm.keys()) : [];
  if (!districts.includes(parts.district)) parts.district = '';
  const subs = dm && parts.district ? dm.get(parts.district) || [] : [];
  const subdistricts = uniq(subs.map((s) => s[0]));
  if (!subdistricts.includes(parts.subdistrict)) parts.subdistrict = '';
  if (parts.subdistrict && zipState !== 'partial') {
    const hit = subs.find((s) => s[0] === parts.subdistrict);
    if (hit) parts.postcode = hit[1];
  }
  return { parts, provinces, districts, subdistricts, zipState, zipDistrictCount: 0 };
}

export type AddressBadge = { tone: 'warn' | 'info' | 'ok'; text: string } | null;

/**
 * ป้ายสถานะต่อท้ายหัวข้อ "ที่อยู่จัดส่ง" — บอกลูกค้าว่าต้องทำอะไรต่อ
 * notice: 'parsed' = แยกจากที่อยู่เดิมให้แล้ว · 'unparsed' = แยกไม่ได้
 */
export function addressStatusBadge(sel: AddressSelection | null, notice: 'parsed' | 'unparsed' | null): AddressBadge {
  if (!sel) return null;
  const p = sel.parts;
  if (isAddressPartsComplete(p)) {
    return notice === 'parsed'
      ? { tone: 'info', text: 'แยกจากที่อยู่เดิมให้แล้ว · ตรวจอีกครั้ง' }
      : { tone: 'ok', text: 'ที่อยู่ครบแล้ว' };
  }
  if (notice === 'unparsed') return { tone: 'warn', text: 'แยกที่อยู่เดิมไม่ได้ · ใส่รหัส ปณ. แล้วเลือกตำบล' };
  if (sel.zipState === 'partial') return { tone: 'warn', text: 'กรอกรหัสไปรษณีย์ให้ครบ 5 หลัก' };
  if (sel.zipState === 'unknown') return { tone: 'warn', text: 'ไม่พบรหัสไปรษณีย์นี้' };
  if (sel.zipState === 'empty' && !p.province) return { tone: 'warn', text: 'กรอกรหัสไปรษณีย์ก่อน' };
  if (!p.province) return { tone: 'warn', text: 'เลือกจังหวัด' };
  if (!p.district) return { tone: 'warn', text: 'เลือกอำเภอ / เขต' };
  if (!p.subdistrict) return { tone: 'warn', text: 'เลือกตำบล / แขวง' };
  return { tone: 'warn', text: 'ใส่บ้านเลขที่ ถนน' };
}

/** ช่องรหัสไปรษณีย์ต้องเรืองแสงไหม — ยังไม่มีรหัสที่ถูกต้อง */
export function zipNeedsAttention(sel: AddressSelection | null): boolean {
  if (!sel) return false;
  return !/^\d{5}$/.test(sel.parts.postcode) || sel.zipState === 'unknown';
}

export function samePartsValue(a: AddressParts, b: AddressParts): boolean {
  return a.line === b.line && a.subdistrict === b.subdistrict && a.district === b.district
    && a.province === b.province && a.postcode === b.postcode;
}

// ────────────────────────── แยกข้อความที่อยู่เดิม ──────────────────────────

const SUB_PREFIX = ['ตำบล', 'แขวง', 'ต.', 'ต '];
const DIST_PREFIX = ['อำเภอ', 'เขต', 'อ.', 'อ '];
const PROV_PREFIX = ['จังหวัด', 'จ.', 'จ '];

type Hit = { index: number; end: number; prefixed: boolean };

/** หาชื่อในข้อความ — ชอบตำแหน่งที่มีคำนำหน้า · คืนตำแหน่งรวมคำนำหน้า */
function findName(text: string, name: string, prefixes: string[]): Hit | null {
  if (!name) return null;
  let best: Hit | null = null;
  for (const pre of prefixes) {
    const needle = pre + name;
    const i = text.lastIndexOf(needle);
    if (i >= 0 && (!best || i > best.index)) best = { index: i, end: i + needle.length, prefixed: !!pre };
  }
  if (best) return best;
  const i = text.lastIndexOf(name);
  return i >= 0 ? { index: i, end: i + name.length, prefixed: false } : null;
}

function overlaps(a: Hit | null, b: Hit | null): boolean {
  return !!a && !!b && a.index < b.end && b.index < a.end;
}

const TRAIL_RE = /[\s,]*(ตำบล|แขวง|อำเภอ|เขต|จังหวัด|ต\.|อ\.|จ\.)?[\s,]*$/;

/**
 * แยกข้อความที่อยู่เดิมเป็นช่อง — ต้องมั่นใจเท่านั้น (เจอรหัสไปรษณีย์ + ตำบลที่อยู่ในรหัสนั้นไม่กำกวม)
 * ไม่มั่นใจ = null (ใช้ข้อความเดิมต่อ ห้ามเดา)
 */
export function parseThaiAddress(db: ThaiAddressDb, text: unknown): AddressParts | null {
  const t = String(text || '').replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  const zipMatches = Array.from(t.matchAll(/(?:^|\D)(\d{5})(?!\d)/g));
  for (let zi = zipMatches.length - 1; zi >= 0; zi--) {
    const zip = zipMatches[zi][1];
    const cands = db.byZip.get(zip);
    if (!cands) continue;
    const zipIndex = (zipMatches[zi].index || 0) + zipMatches[zi][0].indexOf(zip);
    const scored = cands
      .map((c) => {
        const distName = c.district;
        let dist = findName(t, distName, DIST_PREFIX);
        // "อ.เมือง" ย่อของ "เมืองเชียงใหม่"
        if (!dist && distName.startsWith('เมือง')) dist = findName(t, 'เมือง', ['อำเภอ', 'อ.']);
        const prov = c.province === BANGKOK
          ? findName(t, BANGKOK, PROV_PREFIX) || findName(t, 'กรุงเทพฯ', ['']) || findName(t, 'กรุงเทพ', ['']) || findName(t, 'กทม', [''])
          : findName(t, c.province, PROV_PREFIX);
        let sub = findName(t, c.subdistrict, SUB_PREFIX);
        // ตำบลชื่อเดียวกับอำเภอ/จังหวัด (เช่น แขวงคลองเตย เขตคลองเตย) — ห้ามนับคำในชื่ออำเภอเป็นตำบล
        if (sub && !sub.prefixed && (overlaps(sub, dist) || overlaps(sub, prov))) sub = null;
        const score = (sub ? (sub.prefixed ? 4 : 3) : 0) + (dist ? 2 : 0) + (prov ? 1 : 0);
        return { c, sub, dist, prov, score };
      })
      .filter((s) => s.sub && s.sub.index < zipIndex);
    if (!scored.length) continue;
    // คะแนนสูงก่อน · เท่ากัน = ตำบลที่อยู่ท้ายข้อความ (ใกล้รหัสไปรษณีย์) ก่อน
    // ตำบลที่ยาวกว่าชนะเมื่อชื่อหนึ่งเป็นส่วนของอีกชื่อ (เช่น "คลองตัน" ใน "คลองตันเหนือ")
    const subEnd = (s: (typeof scored)[number]) => (s.sub ? s.sub.end : -1);
    scored.sort((a, b) => b.score - a.score || subEnd(b) - subEnd(a) || b.c.subdistrict.length - a.c.subdistrict.length);
    const best = scored[0];
    const second = scored[1];
    if (second && second.score === best.score && subEnd(second) === subEnd(best)
      && second.c.subdistrict.length === best.c.subdistrict.length) continue; // กำกวม
    // รหัสเดียวมีหลายอำเภอ ต้องเจอชื่ออำเภอด้วย ไม่งั้นไม่เดา
    if (!best.dist && cands.some((c) => c.district !== best.c.district)) continue;
    const cut = Math.min(...[best.sub, best.dist, best.prov].filter((h): h is Hit => !!h).map((h) => h.index), zipIndex);
    const line = t.slice(0, cut).replace(TRAIL_RE, '').trim();
    const tail = t.slice(zipIndex + 5).trim();
    return {
      line: clean(tail ? `${line} ${tail}` : line, LIMITS.line),
      subdistrict: best.c.subdistrict,
      district: best.c.district,
      province: best.c.province,
      postcode: zip,
    };
  }
  return parseWithoutZip(db, t);
}

/**
 * ไม่มีรหัสไปรษณีย์ในข้อความ — แยกได้เมื่อเจอครบทั้ง ตำบล + อำเภอ + จังหวัด และมีคำตอบเดียวเท่านั้น
 * (เช่น "… คลองจั่น บางกะปิ กทม.") แล้วเติมรหัสไปรษณีย์จากฐานข้อมูล · กำกวม = null
 */
function parseWithoutZip(db: ThaiAddressDb, t: string): AddressParts | null {
  type Found = { e: ThaiAddressEntry; sub: Hit; dist: Hit; prov: Hit; score: number };
  const found: Found[] = [];
  for (const e of db.entries) {
    const sub0 = findName(t, e.subdistrict, SUB_PREFIX);
    if (!sub0) continue;
    let dist = findName(t, e.district, DIST_PREFIX);
    if (!dist && e.district.startsWith('เมือง')) dist = findName(t, 'เมือง', ['อำเภอ', 'อ.']);
    const prov = e.province === BANGKOK
      ? findName(t, BANGKOK, PROV_PREFIX) || findName(t, 'กรุงเทพฯ', ['']) || findName(t, 'กรุงเทพ', ['']) || findName(t, 'กทม', [''])
      : findName(t, e.province, PROV_PREFIX);
    if (!dist || !prov) continue;
    let sub: Hit | null = sub0;
    if (!sub.prefixed && (overlaps(sub, dist) || overlaps(sub, prov))) sub = null;
    if (!sub) continue;
    found.push({ e, sub, dist, prov, score: (sub.prefixed ? 2 : 0) + (dist.prefixed ? 1 : 0) });
  }
  if (!found.length) return null;
  // ตำบลชื่อยาวกว่าชนะเมื่อชื่อหนึ่งเป็นส่วนของอีกชื่อ (คลองตัน ใน คลองตันเหนือ)
  found.sort((a, b) => b.score - a.score || b.e.subdistrict.length - a.e.subdistrict.length);
  const best = found[0];
  const rivals = found.filter((f) => f.score === best.score && f.e.subdistrict.length === best.e.subdistrict.length);
  const distinct = new Set(rivals.map((f) => `${f.e.province}|${f.e.district}|${f.e.subdistrict}`));
  if (distinct.size > 1) return null;
  const cut = Math.min(best.sub.index, best.dist.index, best.prov.index);
  const line = t.slice(0, cut).replace(TRAIL_RE, '').trim();
  if (!line) return null;
  return {
    line: clean(line, LIMITS.line),
    subdistrict: best.e.subdistrict,
    district: best.e.district,
    province: best.e.province,
    postcode: best.e.postcode,
  };
}
